// src/pages/client_pets/PetsVaccines.js

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Upload, X, Plus, ChevronDown } from "lucide-react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import config from "../../config";

const S3_BASE = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

/* ---------------- DATE HELPERS ---------------- */
const parseDateParts = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    if (isNaN(dateStr.getTime())) return null;
    return {
      year: dateStr.getFullYear(),
      month: dateStr.getMonth() + 1,
      day: dateStr.getDate(),
    };
  }
  const cleanStr = String(dateStr).trim().split("T")[0];
  const parts = cleanStr.split(/[-/]/).map(Number);
  if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
    if (String(parts[0]).length === 4) {
      // YYYY-MM-DD
      return { year: parts[0], month: parts[1], day: parts[2] };
    }
    if (String(parts[2]).length === 4) {
      // DD-MM-YYYY
      return { year: parts[2], month: parts[1], day: parts[0] };
    }
  }
  const dt = new Date(dateStr);
  if (!isNaN(dt.getTime())) {
    return {
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
      day: dt.getDate(),
    };
  }
  return null;
};

const parseDateToTime = (val) => {
  const p = parseDateParts(val);
  if (!p) return null;
  const dt = new Date(p.year, p.month - 1, p.day);
  return isNaN(dt.getTime()) ? null : dt.getTime();
};

const formatDate = (dateStr) => {
  const p = parseDateParts(dateStr);
  if (!p) return typeof dateStr === "string" && dateStr.trim() ? dateStr : "—";
  const dd = String(p.day).padStart(2, "0");
  const mm = String(p.month).padStart(2, "0");
  const yyyy = String(p.year);
  return `${dd}-${mm}-${yyyy}`;
};

/* ---------------- ADD MONTHS HELPER ---------------- */
const addMonths = (dateStr, months) => {
  const p = parseDateParts(dateStr);
  if (!p) return "";
  const d = new Date(p.year, p.month - 1, p.day);
  d.setMonth(d.getMonth() + months);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isExpired = (dateStr) => {
  const p = parseDateParts(dateStr);
  if (!p) return false;

  const today = new Date();
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return new Date(p.year, p.month - 1, p.day) < todayDate;
};

/* ---------------- FILE VIEWER MODAL ---------------- */
const FileViewerModal = ({ url, onClose }) => {
  if (!url) return null;

  const isPdf =
    url.toLowerCase().includes(".pdf") ||
    url.toLowerCase().includes("application/pdf");

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl w-[95%] max-w-3xl h-[85%] shadow-xl flex flex-col overflow-hidden">
        {/* HEADER */}
        <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
          <h2 className="text-lg font-semibold">
            {isPdf ? "PDF Viewer" : "Image Preview"}
          </h2>

          <button
            onClick={onClose}
            className="text-gray-700 hover:text-black text-4xl leading-none"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100">
          {isPdf ? (
            <iframe src={url} title="PDF Viewer" className="w-full h-full" />
          ) : (
            <img
              src={url}
              className="max-w-full max-h-full object-contain"
              alt="Preview"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const DEFAULT_MASTER_VACCINES = [
  "Parvovirus",
  "Hepatitis",
  "Distemper",
  "Rabies",
  "Parainfluenza",
  "Bordetella",
  "DHPP",
];

/* ---------------- MAIN COMPONENT ---------------- */
export default function VaccinesForm({ pet, vendorId, onVaccineAdded }) {
  const petId = pet?._id;
  const [masterVaccines, setMasterVaccines] = useState(DEFAULT_MASTER_VACCINES);
  const [vaccineSearchQuery, setVaccineSearchQuery] = useState("");
  const [vaccineDropdownOpen, setVaccineDropdownOpen] = useState(false);
  const [addingVaccineLoading, setAddingVaccineLoading] = useState(false);
  const vaccineDropdownRef = useRef(null);

  const [vaccines, setVaccines] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("record"); // "record" | "createMaster"
  const [newMasterVaccineName, setNewMasterVaccineName] = useState("");
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState(null);

  const fileInputRef = useRef(null);
  const [currentVaccine, setCurrentVaccine] = useState(null);

  const filteredVaccineOptions = React.useMemo(() => {
    const query = vaccineSearchQuery.trim().toLowerCase();
    if (!query) return masterVaccines;
    return masterVaccines.filter((opt) =>
      opt.toLowerCase().includes(query),
    );
  }, [masterVaccines, vaccineSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        vaccineDropdownRef.current &&
        !vaccineDropdownRef.current.contains(event.target)
      ) {
        setVaccineDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchVaccines = useCallback(async () => {
    try {
      const res = await httpService.get("/vendorpet/getvaccines");
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      const apiVaccineNames = list
        .map((item) => {
          if (typeof item === "string") return item.trim();
          if (item && typeof item === "object") {
            return (
              item.name ||
              item.vaccine ||
              item.vaccine_name ||
              item.label ||
              ""
            )
              .toString()
              .trim();
          }
          return "";
        })
        .filter(Boolean);

      setMasterVaccines((prev) => [
        ...new Set([
          ...DEFAULT_MASTER_VACCINES,
          ...apiVaccineNames,
          ...prev,
        ]),
      ]);
    } catch (err) {
      console.warn("Failed to fetch vaccines", err);
    }
  }, []);

  useEffect(() => {
    fetchVaccines();
  }, [fetchVaccines]);

  useEffect(() => {
    if (pet?.vaccination_records && pet.vaccination_records.length > 0) {
      const recordVaccines = pet.vaccination_records
        .map((r) => r.vaccine || r.name)
        .filter(Boolean);
      if (recordVaccines.length > 0) {
        setMasterVaccines((prev) => {
          const combined = [...new Set([...prev, ...recordVaccines])];
          return combined.length !== prev.length ? combined : prev;
        });
      }
    }
  }, [pet?.vaccination_records]);

  /* ---------------- LOAD EXISTING VACCINES ---------------- */
  useEffect(() => {
    if (!pet?.vaccination_records) {
      setVaccines([]);
      return;
    }

    const parseDateStr = (val) => {
      if (!val) return "";
      if (typeof val === "string") return val.split("T")[0];
      if (val instanceof Date) return val.toISOString().split("T")[0];
      return "";
    };

    setVaccines(
      pet.vaccination_records.map((v) => {
        const rawDate =
          v.date_given ||
          v.date ||
          v.given_date ||
          v.vaccination_date ||
          v.administered_date ||
          v.createdAt;
        const rawNextDue = v.next_due || v.due_date || v.nextDueDate;
        return {
          ...v,
          id: v._id,
          name: v.vaccine || v.name,
          date_given: parseDateStr(rawDate),
          next_due: parseDateStr(rawNextDue),
          administered_by: v.administered_by || "",
          notes: v.notes || "",
          documents: Array.isArray(v.documents)
            ? v.documents.map((d) => ({
                name: d.name,
                url: d.url.startsWith("http") ? d.url : `${S3_BASE}/${d.url}`,
              }))
            : [],
        };
      })
    );
  }, [pet]);

  /* ---------------- DRAWER CONTROLS ---------------- */
  const openDrawer = (vaccineName) => {
    setDrawerMode("record");
    setNewMasterVaccineName("");
    const selectedVaccine =
      typeof vaccineName === "string" ? vaccineName : "";
    setCurrentVaccine({
      id: `temp-${Date.now()}`,
      name: selectedVaccine,
      date_given: "",
      next_due: "",
      administered_by: "",
      notes: "",
      documents: [],
    });
    setVaccineSearchQuery(selectedVaccine || "");
    setVaccineDropdownOpen(false);
    setDrawerOpen(true);
  };

  const openCreateVaccineDrawer = () => {
    setDrawerMode("createMaster");
    setNewMasterVaccineName("");
    setCurrentVaccine({
      id: `temp-${Date.now()}`,
      name: "",
      date_given: "",
      next_due: "",
      administered_by: "",
      notes: "",
      documents: [],
    });
    setVaccineSearchQuery("");
    setVaccineDropdownOpen(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setCurrentVaccine(null);
    setIsDragging(false);
    setVaccineSearchQuery("");
    setVaccineDropdownOpen(false);
    setDrawerMode("record");
    setNewMasterVaccineName("");
  };

  const handleAddMasterVaccine = async () => {
    const trimmed = String(newMasterVaccineName || "").trim();
    if (!trimmed) {
      toast.error("Please enter a vaccine name.", { position: "top-center" });
      return;
    }

    const existing = masterVaccines.find(
      (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      toast.error(`Vaccine "${existing}" already exists.`, {
        position: "top-center",
      });
      return;
    }

    try {
      setAddingVaccineLoading(true);
      const payload = { name: trimmed };
      await httpService.post("/vendorpet/addvaccinemaster", payload);
      toast.success(`Vaccine "${trimmed}" added successfully.`, {
        position: "top-center",
      });
      await fetchVaccines();
      setMasterVaccines((prev) =>
        prev.some((o) => o.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed],
      );
      closeDrawer();
    } catch (err) {
      console.error("Failed to add vaccine:", err);
      toast.error(
        err?.response?.data?.message || `Failed to add vaccine "${trimmed}".`,
        { position: "top-center" },
      );
    } finally {
      setAddingVaccineLoading(false);
    }
  };

  const handleCreateCustomVaccine = async (customName) => {
    const trimmed = String(customName || "").trim();
    if (!trimmed) return;

    const existing = masterVaccines.find(
      (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      handleInput("name", existing);
      setVaccineSearchQuery(existing);
      setVaccineDropdownOpen(false);
      return;
    }

    try {
      setAddingVaccineLoading(true);
      const payload = { name: trimmed };
      await httpService.post("/vendorpet/addvaccinemaster", payload);
      toast.success(`Vaccine "${trimmed}" added successfully.`, {
        position: "top-center",
      });
      await fetchVaccines();
      setMasterVaccines((prev) =>
        prev.some((o) => o.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed],
      );
      handleInput("name", trimmed);
      setVaccineSearchQuery(trimmed);
      setVaccineDropdownOpen(false);
    } catch (err) {
      console.error("Failed to add vaccine:", err);
      toast.error(
        err?.response?.data?.message || `Failed to add vaccine "${trimmed}".`,
        { position: "top-center" },
      );
    } finally {
      setAddingVaccineLoading(false);
    }
  };

  const handleInput = (field, value) => {
    setCurrentVaccine((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-set next_due when date_given changes (11 months later)
      if (field === "date_given" && value) {
        updated.next_due = addMonths(value, 11);
      }
      return updated;
    });
  };

  /* ---------------- FILE UPLOAD ---------------- */
  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("module", "vendors");
    formData.append("docType", "vaccineRecord");
    formData.append("vendorId", vendorId);
    formData.append("file", file);

    try {
      const res = await httpService.post("/upload/fileUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const s3Key =
        res?.data?.data?.s3Key || res?.data?.s3Key || res?.data?.data?.key;

      const s3Url =
        res?.data?.data?.s3Url || res?.data?.data?.url || `${S3_BASE}/${s3Key}`;

      return { name: file.name, url: s3Url };
    } catch (err) {
      toast.error("File upload failed");
      return null;
    }
  };

  const handleFileUpload = async (files) => {
    const uploadedDocs = [];

    for (const file of files) {
      const uploaded = await uploadSingleFile(file);
      if (uploaded) uploadedDocs.push(uploaded);
    }

    if (uploadedDocs.length > 0) {
      setCurrentVaccine((prev) => ({
        ...prev,
        documents: [...prev.documents, ...uploadedDocs],
      }));
    }
  };

  /* ---------------- SAVE VACCINE ---------------- */
  const handleSave = async () => {
    if (!petId || !currentVaccine) return;

    if (!currentVaccine.name || currentVaccine.name === "Select Vaccine")
      return toast.error("Please select or enter a vaccine name");

    // date_given is now optional – remove the check

    setSaving(true);

    const payload = {
      vaccine: currentVaccine.name,
      date_given: currentVaccine.date_given || null,
      next_due: currentVaccine.next_due || null,
      administered_by: currentVaccine.administered_by,
      notes: currentVaccine.notes,
      documents: currentVaccine.documents,
    };

    try {
      const response = await httpService.post(
        `/vendorpet/addvaccine/${petId}`,
        payload
      );

      const saved = response?.data?.data || response?.data;

      const newVaccine = {
        id: saved._id,
        name: saved.vaccine,
        date_given: saved.date_given?.split("T")[0],
        next_due: saved.next_due?.split("T")[0],
        administered_by: saved.administered_by,
        notes: saved.notes,
        documents: saved.documents || [],
      };

      setVaccines((prev) => [...prev, newVaccine]);
      setMasterVaccines((prev) =>
        prev.includes(newVaccine.name) ? prev : [...prev, newVaccine.name],
      );
      onVaccineAdded?.({
        ...pet,
        vaccinations: Array.from(
          new Set([...(pet?.vaccinations || []), newVaccine.name]),
        ),
        vaccination_records: [...(pet?.vaccination_records || []), saved],
      });

      toast.success("Vaccine saved successfully!");
      closeDrawer();
    } catch (err) {
      toast.error("Failed to save vaccine");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="py-1">

      <div className="bg-white rounded-xl shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead className="bg-gray-50 text-gray-700 text-sm uppercase tracking-wide">
              <tr>
                <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                  Vaccine
                </th>
                <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                  Given On
                </th>
                <th className="px-6 py-4 font-semibold text-left border-b border-gray-200 whitespace-nowrap">
                  Next Due
                </th>
                <th className="px-6 py-4 font-semibold text-left border-b border-gray-200">
                  Upload File
                </th>
              </tr>
            </thead>
            <tbody>
              {masterVaccines.map((name, idx) => {
                const normName = String(name || "").trim().toLowerCase();
                const list = (vaccines || []).filter((x) => {
                  const xName = String(x?.name || "").trim().toLowerCase();
                  if (xName === normName) return true;
                  if (
                    (normName === "dhpp" || normName === "dhppi") &&
                    (xName === "dhpp" || xName === "dhppi")
                  )
                    return true;
                  return false;
                });

                // Extract all valid recorded doses with actual dates (display only doses with recorded dates)
                const validRecords = list
                  .map((r) => {
                    const rawDate =
                      r?.date_given ||
                      r?.date ||
                      r?.given_date ||
                      r?.vaccination_date ||
                      r?.administered_date ||
                      r?.createdAt;
                    const parsedTime = parseDateToTime(rawDate);
                    return {
                      ...r,
                      rawDate,
                      parsedTime,
                    };
                  })
                  .filter((r) => r.parsedTime !== null && !isNaN(r.parsedTime));

                // Sort chronologically ascending (oldest first) to assign chronological dose numbers
                const chronologicalDoses = [...validRecords].sort(
                  (a, b) => a.parsedTime - b.parsedTime,
                );

                // Assign dose labels (Dose 1, Dose 2, Dose 3...)
                const labeledDoses = chronologicalDoses.map((r, i) => {
                  let label = `Dose ${i + 1}`;
                  const explicit = r?.dose_number || r?.dose_no || r?.dose;
                  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== "") {
                    const s = String(explicit).trim();
                    if (/^dose\s*\d+/i.test(s)) {
                      label = s.replace(/^dose\s*/i, "Dose ");
                    } else if (!isNaN(Number(s))) {
                      label = `Dose ${s}`;
                    } else {
                      label = s;
                    }
                  }
                  return {
                    ...r,
                    doseLabel: label,
                    formattedDate: formatDate(r.rawDate),
                  };
                });

                // Sort descending so the latest vaccination date is displayed first
                const displayDoses = [...labeledDoses].sort(
                  (a, b) => b.parsedTime - a.parsedTime,
                );

                // Determine latest dose record based strictly on actual recorded date (maximum parsedTime)
                const latestDose = displayDoses.length
                  ? displayDoses[0]
                  : list.length
                  ? list[list.length - 1]
                  : null;

                const v = latestDose;

                // Compute next_due if missing (11 months from latest dose date)
                let displayNextDue = v?.next_due;
                const latestDateGiven =
                  v?.rawDate ||
                  v?.date_given ||
                  v?.date ||
                  v?.given_date;
                if (!displayNextDue && latestDateGiven) {
                  displayNextDue = addMonths(latestDateGiven, 11);
                }
                const expired = isExpired(displayNextDue);
                const allDocs = list.flatMap((item) => item.documents || []).filter(Boolean);

                return (
                  <tr
                    key={name}
                    className={`${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } ${expired ? "text-red-600" : ""} hover:bg-indigo-50 transition`}
                    title={expired ? "Vaccine is expired" : undefined}
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                            expired
                              ? "border-red-500 bg-red-50 text-red-700"
                              : v
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-gray-300 bg-white text-gray-700"
                          }`}
                          title={expired ? "Vaccine is expired" : undefined}
                          aria-label={expired ? `${name}, vaccine is expired` : name}
                        >
                          {v && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white text-xs">
                              ✓
                            </span>
                          )}
                          <span>{name}</span>
                        </span>

                        {displayDoses.length > 0 && (
                          <span
                            className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold border border-indigo-200"
                            title={`${displayDoses.length} doses recorded for ${name}`}
                          >
                            {displayDoses.length} {displayDoses.length === 1 ? "dose" : "doses"}
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => openDrawer(name)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-xs cursor-pointer flex-shrink-0"
                          title={`Add new dose for ${name}`}
                          aria-label={`Add new dose for ${name}`}
                        >
                          <Plus size={14} strokeWidth={2.5} />
                        </button>
                      </div>
                    </td>

                    {/* Given On: Doses listed latest first */}
                    <td className="px-6 py-4 text-gray-700">
                      {displayDoses.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {displayDoses.map((d, dIdx) => (
                            <div
                              key={dIdx}
                              className={`text-sm whitespace-nowrap ${
                                dIdx === 0
                                  ? "font-semibold text-gray-900"
                                  : "text-gray-600"
                              }`}
                              title={
                                d.administered_by
                                  ? `Administered by: ${d.administered_by}`
                                  : undefined
                              }
                            >
                              {d.doseLabel} - {d.formattedDate}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>

                    <td className={`px-6 py-4 ${expired ? "text-red-600 font-medium" : "text-gray-700"}`}>
                      {v ? (displayNextDue ? formatDate(displayNextDue) : "-") : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {allDocs.length > 0 ? (
                          allDocs.map((doc, i) => {
                            const isPdf =
                              doc.name?.toLowerCase().endsWith(".pdf") ||
                              doc.url?.toLowerCase().includes(".pdf");
                            return (
                              <div key={i}>
                                <div
                                  onClick={() => setFileViewUrl(doc.url)}
                                  className="h-14 w-14 border rounded-lg cursor-pointer shadow-sm hover:shadow-md transition bg-white flex items-center justify-center overflow-hidden"
                                  title={doc.name || "Vaccination Certificate"}
                                >
                                  {isPdf ? (
                                    <span className="text-xs font-semibold">PDF</span>
                                  ) : (
                                    <img
                                      src={doc.url}
                                      alt={doc.name || "Vaccination Certificate"}
                                      className="h-full w-full object-cover"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Vaccine Button below the list on the left side */}
      <div className="flex justify-start mt-3">
        <button
          type="button"
          onClick={() => openCreateVaccineDrawer()}
          className="text-indigo-600 hover:text-indigo-800 font-bold text-sm transition cursor-pointer py-1"
        >
          + Add New Vaccine
        </button>
      </div>

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[998]"
          onClick={closeDrawer}
        />
      )}

      {/* DRAWER */}
      {drawerOpen && currentVaccine && (
        <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col z-[999]">
          <div className="p-5 border-b flex justify-between items-center bg-gray-50">
            <h2 className="text-xl font-semibold">
              {drawerMode === "createMaster" ? "Add New Vaccine" : "Add Vaccine"}
            </h2>
            <X className="cursor-pointer w-6 h-6" onClick={closeDrawer} />
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {drawerMode === "createMaster" ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Vaccine Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newMasterVaccineName}
                  onChange={(e) => setNewMasterVaccineName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!addingVaccineLoading && newMasterVaccineName.trim()) {
                        handleAddMasterVaccine();
                      }
                    }
                  }}
                  placeholder="Enter vaccine name..."
                  autoFocus
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                />
              </div>
            ) : (
              <>
                {/* FORM FIELDS */}
                <div className="space-y-2 relative" ref={vaccineDropdownRef}>
              <label className="block text-sm font-semibold text-gray-700">
                Vaccine Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={vaccineSearchQuery}
                  onChange={(e) => {
                    const val = e.target.value;
                    setVaccineSearchQuery(val);
                    setVaccineDropdownOpen(true);
                    handleInput("name", val);
                  }}
                  onFocus={() => setVaccineDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const query = vaccineSearchQuery.trim();
                      if (!query) return;
                      const exactMatch = masterVaccines.find(
                        (opt) => opt.toLowerCase() === query.toLowerCase(),
                      );
                      if (exactMatch) {
                        handleInput("name", exactMatch);
                        setVaccineSearchQuery(exactMatch);
                        setVaccineDropdownOpen(false);
                      } else {
                        handleCreateCustomVaccine(query);
                      }
                    }
                  }}
                  placeholder="Search or type custom vaccine..."
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white pr-14"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-gray-400">
                  {vaccineSearchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setVaccineSearchQuery("");
                        handleInput("name", "");
                        setVaccineDropdownOpen(true);
                      }}
                      className="hover:text-gray-600 p-0.5 rounded-full"
                      title="Clear"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setVaccineDropdownOpen((prev) => !prev)}
                    className="hover:text-gray-600 p-0.5"
                  >
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        vaccineDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Dropdown Menu */}
              {vaccineDropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                  {filteredVaccineOptions.length > 0 ? (
                    <div className="py-1 divide-y divide-gray-50">
                      {filteredVaccineOptions.map((opt) => {
                        const isSelected =
                          currentVaccine.name?.toLowerCase() ===
                          opt.toLowerCase();
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              handleInput("name", opt);
                              setVaccineSearchQuery(opt);
                              setVaccineDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                              isSelected
                                ? "bg-indigo-50 text-indigo-700 font-semibold"
                                : "hover:bg-gray-50 text-gray-700"
                            }`}
                          >
                            <span>{opt}</span>
                            {isSelected && (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {vaccineSearchQuery.trim() &&
                        !masterVaccines.some(
                          (o) =>
                            o.toLowerCase() ===
                            vaccineSearchQuery.trim().toLowerCase(),
                        ) && (
                          <button
                            type="button"
                            onClick={() =>
                              handleCreateCustomVaccine(
                                vaccineSearchQuery.trim(),
                              )
                            }
                            disabled={addingVaccineLoading}
                            className="w-full text-left px-4 py-3 text-sm font-medium text-indigo-600 bg-indigo-50/70 hover:bg-indigo-100 flex items-center gap-2 transition-colors border-t border-indigo-100"
                          >
                            <Plus
                              size={16}
                              className="text-indigo-600 flex-shrink-0"
                            />
                            <span>
                              {addingVaccineLoading
                                ? "Adding vaccine..."
                                : `Create "${vaccineSearchQuery.trim()}"`}
                            </span>
                          </button>
                        )}
                    </div>
                  ) : (
                    <div className="p-4 text-sm text-gray-500 flex flex-col gap-2">
                      <span>No matching vaccines found.</span>
                      {vaccineSearchQuery.trim() && (
                        <button
                          type="button"
                          onClick={() =>
                            handleCreateCustomVaccine(
                              vaccineSearchQuery.trim(),
                            )
                          }
                          disabled={addingVaccineLoading}
                          className="text-left text-indigo-600 font-medium hover:underline flex items-center gap-1.5"
                        >
                          <Plus size={15} />
                          <span>
                            {addingVaccineLoading
                              ? "Adding vaccine..."
                              : `Create "${vaccineSearchQuery.trim()}"`}
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label>Date Given</label>   {/* removed asterisk */}
              <input
                type="date"
                value={currentVaccine.date_given}
                onChange={(e) => handleInput("date_given", e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            <div className="space-y-2">
              <label>Next Due (Optional)</label>
              <input
                type="date"
                value={currentVaccine.next_due}
                onChange={(e) => handleInput("next_due", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Administered By removed */}

            {/* FILE UPLOAD AREA */}
            <div className="space-y-2">
              <label>Upload Files</label>

              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${
                  isDragging
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-300"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={async (e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  await handleFileUpload(Array.from(e.dataTransfer.files));
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="text-indigo-600 mx-auto mb-2" size={24} />
                <p className="font-medium text-indigo-600">
                  Click or Drag files to upload
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
              />

              {/* Thumbnails */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {currentVaccine.documents.map((doc, i) => {
                  const isPdf =
                    doc.name?.toLowerCase().endsWith(".pdf") ||
                    doc.url?.toLowerCase().includes(".pdf");

                  return (
                    <div key={i} className="relative border rounded-lg p-1">
                      <div
                        onClick={() => setFileViewUrl(doc.url)}
                        className="h-20 w-full cursor-pointer bg-gray-50 flex items-center justify-center overflow-hidden"
                      >
                        {isPdf ? (
                          <span className="text-sm font-medium">PDF</span>
                        ) : (
                          <img
                            src={doc.url}
                            alt={doc.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>

                      <button
                        onClick={() =>
                          setCurrentVaccine((prev) => ({
                            ...prev,
                            documents: prev.documents.filter(
                              (_, idx) => idx !== i,
                            ),
                          }))
                        }
                        className="absolute -top-2 -right-2 h-6 w-6 bg-red-600 text-white rounded-full flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
              </>
            )}
          </div>

          {/* ACTION BUTTONS */}
          {drawerMode === "createMaster" ? (
            <div className="p-4 border-t flex gap-3 bg-gray-50">
              <button
                type="button"
                onClick={closeDrawer}
                className="w-1/2 border py-3 rounded-xl hover:bg-gray-100 font-medium transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddMasterVaccine}
                disabled={addingVaccineLoading || !newMasterVaccineName.trim()}
                className="w-1/2 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-400 transition"
              >
                {addingVaccineLoading ? "Adding..." : "Add New Vaccine"}
              </button>
            </div>
          ) : (
            <div className="p-4 border-t flex gap-3 bg-gray-50">
              <button
                onClick={closeDrawer}
                className="w-1/2 border py-3 rounded-xl hover:bg-gray-100 font-medium transition"
              >
                Cancel
              </button>
              <button
                disabled={
                  saving ||
                  !currentVaccine.name ||
                  currentVaccine.name === "Select Vaccine"
                }
                onClick={handleSave}
                className="w-1/2 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-400 font-medium transition"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* FILE VIEWER MODAL */}
      {fileViewUrl && (
        <FileViewerModal
          url={fileViewUrl}
          onClose={() => setFileViewUrl(null)}
        />
      )}
    </div>
  );
}